import { useEffect, useState, useCallback } from "react";

/**
 * Tiny localStorage-backed boolean/string preference hook with cross-tab
 * (and cross-component) sync. Lets us keep UI-only preferences (show
 * assignee chips on stage pills, timeline filter default, etc.) out of
 * the backend while still updating every mount when the user flips them
 * in Settings.
 *
 *   const [showAssignees, setShowAssignees] = useUiPref("pipeline.showAssignees", false);
 *
 * Notes:
 *   • Value can be boolean or string. Anything else stays as-is on read.
 *   • Same-tab writes broadcast via a custom "wc-uipref" CustomEvent because
 *     the native `storage` event only fires in OTHER tabs.
 */
export function useUiPref(key, fallback) {
  const [value, setValue] = useState(() => readPref(key, fallback));

  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.key && e.detail.key !== key) return;
      setValue(readPref(key, fallback));
    };
    window.addEventListener("wc-uipref", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("wc-uipref", handler);
      window.removeEventListener("storage", handler);
    };
  }, [key, fallback]);

  const write = useCallback((next) => {
    const resolved = typeof next === "function" ? next(readPref(key, fallback)) : next;
    try {
      localStorage.setItem(`wc.uipref.${key}`, JSON.stringify(resolved));
    } catch {
      /* quota / private mode — fall back to in-memory only */
    }
    setValue(resolved);
    window.dispatchEvent(new CustomEvent("wc-uipref", { detail: { key } }));
  }, [key, fallback]);

  return [value, write];
}

function readPref(key, fallback) {
  try {
    const raw = localStorage.getItem(`wc.uipref.${key}`);
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}
